// src/HomePage/Game/Werewolf/WerewolfGameView.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useWerewolf } from './WerewolfContext';
import { ROLE_DEFINITIONS } from './roleEngine';
import WerewolfRoomView from './WerewolfRoomView';
import './WerewolfGameView.css';

// Import file âm thanh (Điều chỉnh lại đường dẫn nếu thư mục sounds nằm ở vị trí khác)
import gaGaySound from './sounds/ga_gay.mp3';
import soiHuSound from './sounds/soi_hu.mp3';

const WerewolfGameView = () => {
  const { currentRoom, activeUid, leaveRoom, updateGameState, resetRoomToLobby } = useWerewolf();

  const [showRoleCard, setShowRoleCard] = useState(false);
  const [cupidSelection, setCupidSelection] = useState([]);
  const [showLogsModal, setShowRulesLogsModal] = useState(false);
  const [returnedToLobby, setReturnedToLobby] = useState(false);

  const [expandedDays, setExpandedDays] = useState({});
  const [seerModalData, setSeerModalData] = useState(null);
  const [infoModalMessage, setInfoModalMessage] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);

  // ⏱️ State lưu số giây còn lại
  const [timeLeft, setTimeLeft] = useState(null);

  // Ref quản lý Audio đang phát
  const currentAudioRef = useRef(null);
  const audioTimerRef = useRef(null);

  const gameState = currentRoom?.gameState;
  const playersMap = currentRoom?.players || {};
  const playersList = Object.values(playersMap);
  const myPlayer = playersMap[activeUid];
  const isHost = currentRoom?.hostUid === activeUid;

  const currentPhase = gameState?.phase;
  const dayNumber = gameState?.dayNumber || 1;
  const nightActions = gameState?.nightActions || {};
  const votes = gameState?.votes || {};
  const gameLogs = gameState?.gameLogs || [];

  const seerHasActedThisNight = Boolean(nightActions?.seerCheckedUid);
  const guardHasActedThisNight = Boolean(nightActions?.protectedUid);
  const witchHasActedThisNight = Boolean(nightActions?.witchSaved || nightActions?.witchPoisonedUid);

  const hunterTargetUid = gameState?.hunterTargetUid || null;
  const hunterActedThisNight = Boolean(nightActions?.hunterActedThisNight);
  const hunterActedThisDay = Boolean(gameState?.hunterActedThisDay);

  const wolfVotes = nightActions?.wolfVotes || {};
  const hasCupidInGame = playersList.some(p => p.role === 'CUPID');

  const cupidCouple = nightActions?.cupidCouple || [];

  const isLover = cupidCouple.includes(activeUid) && cupidCouple.length === 2;
  const loverUid = isLover ? cupidCouple.find(uid => uid !== activeUid) : null;
  const loverPlayer = loverUid ? playersMap[loverUid] : null;
  const loverRoleInfo = loverPlayer ? (ROLE_DEFINITIONS[loverPlayer.role] || { name: 'Chưa rõ', icon: '❓', team: 'Chưa rõ' }) : null;

  const isCupidNight1Warning =
    hasCupidInGame &&
    (currentPhase === 'NIGHT_TURN_1' || currentPhase === 'NIGHT_TURN_2') &&
    dayNumber === 1 &&
    myPlayer?.role === 'CUPID' &&
    myPlayer?.isAlive &&
    cupidCouple.length < 2;

  // 🔊 HÀM PHÁT ÂM THANH THEO THỜI GIAN QUY ĐỊNH
  const playSoundEffect = (soundSrc, durationInMs) => {
    // Dừng âm thanh trước đó nếu còn đang phát
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
    }
    if (audioTimerRef.current) {
      clearTimeout(audioTimerRef.current);
    }

    const audio = new Audio(soundSrc);
    currentAudioRef.current = audio;

    audio.play().catch((err) => {
      console.warn("Trình duyệt chặn autoplay âm thanh:", err);
    });

    audioTimerRef.current = setTimeout(() => {
      audio.pause();
      audio.currentTime = 0;
      currentAudioRef.current = null;
    }, durationInMs);
  };

  // 🔊 EFFECT PHÁT ÂM THANH KHI BẮT ĐẦU NGÀY / ĐÊM
  useEffect(() => {
    if (!currentPhase) return;

    if (currentPhase === 'NIGHT_TURN_1') {
      // Ban Đêm bắt đầu (Lượt 1): Phát tiếng sói hú 6 giây
      playSoundEffect(soiHuSound, 6000);
    } else if (currentPhase === 'DAY_DISCUSSION') {
      // Ban Ngày bắt đầu (Thảo luận): Phát tiếng gà gáy 3 giây
      playSoundEffect(gaGaySound, 3000);
    }

    return () => {
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current.currentTime = 0;
      }
      if (audioTimerRef.current) {
        clearTimeout(audioTimerRef.current);
      }
    };
  }, [currentPhase, dayNumber]);

  // 🛠️ HÀM TÍNH MỐC THỜI GIAN KẾT THÚC DỰA VÀO PHASE TỚI
  const calculateNextTurnEndTime = useCallback((nextPhase) => {
    const rules = currentRoom?.gameRules || {};
    let duration = 0;

    if (nextPhase === 'NIGHT_TURN_1' || nextPhase === 'NIGHT_TURN_2') {
      duration = Number(rules.nightDuration) || 0;
    } else if (nextPhase === 'DAY_DISCUSSION') {
      duration = Number(rules.dayDuration) || 0;
    } else if (nextPhase === 'DAY_VOTING') {
      duration = Number(rules.voteDuration) || 0;
    }

    return duration > 0 ? Date.now() + duration * 1000 : null;
  }, [currentRoom?.gameRules]);

  // Lắng nghe sự kiện chuyển sang Lượt 2 để hiện Toast nhắc nhở Thần Tình Yêu
  useEffect(() => {
    if (!gameState) return;

    if (
      currentPhase === 'NIGHT_TURN_2' &&
      dayNumber === 1 &&
      myPlayer?.role === 'CUPID' &&
      myPlayer?.isAlive &&
      (!cupidCouple || cupidCouple.length < 2)
    ) {
      setToastMessage("⚠️ Lượt 2 đã bắt đầu! Bạn vẫn chưa ghép đôi, hãy chọn ngay nếu không sẽ bị tử vong khi trời sáng!");

      const timer = setTimeout(() => setToastMessage(null), 6000);
      return () => clearTimeout(timer);
    }
  }, [gameState, currentPhase, dayNumber, myPlayer, cupidCouple]);

  const toggleDayExpand = (dayNum) => {
    setExpandedDays(prev => ({
      ...prev,
      [dayNum]: !prev[dayNum]
    }));
  };

  const checkGameEnd = (updatedPlayers, currentCouple) => {
    const alivePlayers = Object.values(updatedPlayers).filter(p => p.isAlive);

    const isCoupleActive = currentCouple && currentCouple.length === 2;
    const isCoupleAlive = isCoupleActive && currentCouple.every(uid => updatedPlayers[uid]?.isAlive);

    const coupleRoles = isCoupleAlive ? currentCouple.map(uid => updatedPlayers[uid]?.role) : [];
    const hasWolfInCouple = coupleRoles.includes('WEREWOLF');
    const hasNonWolfInCouple = coupleRoles.some(r => r !== 'WEREWOLF');
    const isCrossTeamCouple = isCoupleAlive && hasWolfInCouple && hasNonWolfInCouple;

    if (isCoupleAlive && alivePlayers.length === 2) {
      return {
        isEnd: true,
        winnerTeam: 'COUPLE',
        message: '🏆 Phe Tình Nhân (Cặp đôi Thần Tình Yêu) đã xuất sắc sống sót đến cuối cùng và giành chiến thắng!'
      };
    }

    const has3rdPartyBlocker = isCrossTeamCouple;

    const aliveWolves = alivePlayers.filter(p => p.role === 'WEREWOLF');
    const aliveNonWolves = alivePlayers.filter(p => p.role !== 'WEREWOLF');

    if (aliveWolves.length === 0) {
      return {
        isEnd: true,
        winnerTeam: 'HUMAN',
        message: '👨‍🌾 Tất cả Ma Sói đã bị tiêu diệt! Phe Dân Làng giành chiến thắng rực rỡ!'
      };
    }

    if (aliveWolves.length >= aliveNonWolves.length && !has3rdPartyBlocker) {
      return {
        isEnd: true,
        winnerTeam: 'WOLF',
        message: '🐺 Phe Ma Sói đã áp đảo số lượng và giành chiến thắng chung cuộc!'
      };
    }

    return { isEnd: false };
  };

  const handleAdvanceToTurn2 = useCallback(async () => {
    if (!isHost) return;

    const updatedPlayers = { ...playersMap };

    const winStatus = checkGameEnd(updatedPlayers, cupidCouple);
    if (winStatus.isEnd) {
      await updateGameState({
        'phase': 'ENDED',
        'turnEndTime': null,
        'winnerTeam': winStatus.winnerTeam,
        'winnerMsg': winStatus.message,
        'players': updatedPlayers
      }, {
        day: dayNumber,
        phase: 'KẾT THÚC',
        content: winStatus.message
      });
      return;
    }

    const bittenUid = nightActions.bittenUid;
    let wolfTargetLog = '';
    if (bittenUid && playersMap[bittenUid]) {
      const target = playersMap[bittenUid];
      const roleName = ROLE_DEFINITIONS[target?.role]?.name || 'Chưa rõ';
      wolfTargetLog = ` Đàn Ma Sói đã thống nhất chọn cắn người chơi ${target.fullName} (${roleName}).`;
    } else {
      wolfTargetLog = ` Đàn Ma Sói đã không cắn ai đêm nay.`;
    }

    await updateGameState({
      'phase': 'NIGHT_TURN_2',
      'turnEndTime': calculateNextTurnEndTime('NIGHT_TURN_2'),
      'players': updatedPlayers
    }, {
      day: dayNumber,
      phase: `Đêm ${dayNumber}`,
      content: `Kết thúc Lượt 1.${wolfTargetLog} Chuyển sang Lượt 2 (Các chức năng đặc biệt thức dậy).`
    });
  }, [isHost, playersMap, cupidCouple, dayNumber, nightActions, calculateNextTurnEndTime, updateGameState]);

  const handleAdvanceToDay = useCallback(async () => {
    if (!isHost) return;

    const bittenUid = nightActions.bittenUid || null;
    const protectedUid = nightActions.protectedUid || null;
    const witchSaved = Boolean(nightActions.witchSaved);
    const witchPoisonedUid = nightActions.witchPoisonedUid || null;

    const deadThisNight = [];
    let hunterPoisonedByWitch = false;

    if (bittenUid && bittenUid !== protectedUid && !witchSaved) {
      deadThisNight.push(bittenUid);
    }

    if (hasCupidInGame && dayNumber === 1) {
      const cupidPlayer = playersList.find(p => p.role === 'CUPID' && p.isAlive);
      if (cupidPlayer && (!cupidCouple || cupidCouple.length < 2) && !deadThisNight.includes(cupidPlayer.uid)) {
        deadThisNight.push(cupidPlayer.uid);
      }
    }

    if (witchPoisonedUid) {
      deadThisNight.push(witchPoisonedUid);
      if (playersMap[witchPoisonedUid]?.role === 'HUNTER') {
        hunterPoisonedByWitch = true;
      }
    }

    if (cupidCouple && cupidCouple.length === 2) {
      const [p1, p2] = cupidCouple;
      if (deadThisNight.includes(p1) && !deadThisNight.includes(p2)) {
        deadThisNight.push(p2);
      } else if (deadThisNight.includes(p2) && !deadThisNight.includes(p1)) {
        deadThisNight.push(p1);
      }
    }

    const hunterPlayer = playersList.find(p => p.role === 'HUNTER');
    const isHunterDeadNight = hunterPlayer && deadThisNight.includes(hunterPlayer.uid);

    let hunterLogMsg = '';
    if (isHunterDeadNight) {
      if (hunterPoisonedByWitch) {
        hunterLogMsg = ` 🧪 Thợ Săn (${hunterPlayer.fullName}) bị Phù Thủy hạ độc nên không thể bắn mục tiêu!`;
      } else {
        const currentHunterTarget = gameState?.hunterTargetUid;
        if (currentHunterTarget && playersMap[currentHunterTarget]?.isAlive && !deadThisNight.includes(currentHunterTarget)) {
          deadThisNight.push(currentHunterTarget);
          const targetP = playersMap[currentHunterTarget];
          const targetRole = ROLE_DEFINITIONS[targetP?.role]?.name || 'Chưa rõ';
          hunterLogMsg = ` 🏹 Thợ Săn (${hunterPlayer.fullName}) tử vong đã nổ súng kéo theo mục tiêu ${targetP.fullName} (${targetRole}) chết cùng!`;
        }
      }
    }

    const updatedPlayers = { ...playersMap };
    deadThisNight.forEach(uid => {
      if (updatedPlayers[uid]) {
        updatedPlayers[uid].isAlive = false;
      }
    });

    const winStatus = checkGameEnd(updatedPlayers, nightActions.cupidCouple);
    if (winStatus.isEnd) {
      await updateGameState({
        'phase': 'ENDED',
        'turnEndTime': null,
        'winnerTeam': winStatus.winnerTeam,
        'winnerMsg': winStatus.message,
        'players': updatedPlayers
      }, {
        day: dayNumber,
        phase: 'KẾT THÚC',
        content: `${winStatus.message}${hunterLogMsg}`
      });
      return;
    }

    const morningSummary = deadThisNight.length === 0
      ? `Sáng Ngày ${dayNumber}: Đêm qua là một đêm bình yên, không ai bị sát hại!`
      : `Sáng Ngày ${dayNumber}: Đêm qua có ${deadThisNight.length} người chơi bị tử vong.${hunterLogMsg}`;

    await updateGameState({
      'phase': 'DAY_DISCUSSION',
      'turnEndTime': calculateNextTurnEndTime('DAY_DISCUSSION'),
      'players': updatedPlayers,
      'hunterActedThisDay': false,
      'nightActions': {
        wolfVotes: {},
        bittenUid: null,
        protectedUid: null,
        seerCheckedUid: null,
        witchSaved: false,
        witchPoisonedUid: null,
        hunterActedThisNight: false,
        cupidCouple: nightActions.cupidCouple || null
      }
    }, {
      day: dayNumber,
      phase: `Sáng Ngày ${dayNumber}`,
      content: morningSummary
    });
  }, [isHost, nightActions, hasCupidInGame, dayNumber, playersList, cupidCouple, playersMap, gameState?.hunterTargetUid, calculateNextTurnEndTime, updateGameState]);

  const handleAdvanceToVoting = useCallback(async () => {
    if (!isHost) return;
    await updateGameState({
      'phase': 'DAY_VOTING',
      'turnEndTime': calculateNextTurnEndTime('DAY_VOTING'),
      'votes': {}
    }, {
      day: dayNumber,
      phase: `Ban Ngày ${dayNumber}`,
      content: `Kết thúc thời gian thảo luận. Chuyển sang giai đoạn Bỏ Phiếu Xử Tử!`
    });
  }, [isHost, dayNumber, calculateNextTurnEndTime, updateGameState]);

  const handleAdvanceToNextNight = useCallback(async () => {
    if (!isHost) return;

    const currentVotes = gameState?.votes || {};
    const voteCounts = {};

    Object.entries(currentVotes).forEach(([voterUid, targetUid]) => {
      if (playersMap[voterUid]?.isAlive && playersMap[targetUid]?.isAlive) {
        voteCounts[targetUid] = (voteCounts[targetUid] || 0) + 1;
      }
    });

    const alivePlayersList = Object.values(playersMap).filter(p => p.isAlive);
    const totalIssuedVotes = alivePlayersList.length;

    let maxVotes = 0;
    let executedUid = null;
    let isTie = false;

    Object.entries(voteCounts).forEach(([targetUid, count]) => {
      if (count > maxVotes) {
        maxVotes = count;
        executedUid = targetUid;
        isTie = false;
      } else if (count === maxVotes && count > 0) {
        isTie = true;
      }
    });

    const updatedPlayers = { ...playersMap };
    const nextDay = dayNumber + 1;
    let resultLog = '';

    const isAbsoluteMajority = maxVotes > (totalIssuedVotes / 2);

    if (executedUid && !isTie && isAbsoluteMajority) {
      updatedPlayers[executedUid].isAlive = false;
      const executedPlayer = playersMap[executedUid];
      const roleName = ROLE_DEFINITIONS[executedPlayer?.role]?.name || 'Chưa rõ';
      resultLog = `🗳️ Kết quả bỏ phiếu: ${executedPlayer?.fullName} (${roleName}) nhận ${maxVotes}/${totalIssuedVotes} phiếu (> 50%), chính thức bị treo cổ!`;

      if (executedPlayer.role === 'HUNTER') {
        const currentHunterTarget = gameState?.hunterTargetUid;
        if (currentHunterTarget && updatedPlayers[currentHunterTarget]?.isAlive) {
          updatedPlayers[currentHunterTarget].isAlive = false;
          const targetP = playersMap[currentHunterTarget];
          const targetRole = ROLE_DEFINITIONS[targetP?.role]?.name || 'Chưa rõ';
          resultLog += ` 🏹 Thợ Săn bị treo cổ đã nổ súng kéo theo mục tiêu ${targetP.fullName} (${targetRole}) chết cùng!`;
        }
      }

      if (cupidCouple && cupidCouple.includes(executedUid)) {
        const partnerUid = cupidCouple.find(uid => uid !== executedUid);
        if (partnerUid && updatedPlayers[partnerUid]?.isAlive) {
          updatedPlayers[partnerUid].isAlive = false;
          resultLog += ` 💔 ${playersMap[partnerUid]?.fullName} đã tự sát chết theo người tình!`;

          if (updatedPlayers[partnerUid]?.role === 'HUNTER') {
            const currentHunterTarget = gameState?.hunterTargetUid;
            if (currentHunterTarget && updatedPlayers[currentHunterTarget]?.isAlive) {
              updatedPlayers[currentHunterTarget].isAlive = false;
              const targetP = playersMap[currentHunterTarget];
              const targetRole = ROLE_DEFINITIONS[targetP?.role]?.name || 'Chưa rõ';
              resultLog += ` 🏹 Thợ Săn (${playersMap[partnerUid]?.fullName}) chết theo tình nhân đã bắn kéo theo ${targetP.fullName} (${targetRole})!`;
            }
          }
        }
      }
    } else if (executedUid && !isTie && !isAbsoluteMajority) {
      const targetPlayer = playersMap[executedUid];
      resultLog = `🛡️ Kết quả bỏ phiếu: ${targetPlayer?.fullName} nhận ${maxVotes}/${totalIssuedVotes} phiếu (≤ 50%). Dân làng không đạt đa số tuyệt đối, giàn treo cổ bị hủy bỏ!`;
    } else if (isTie) {
      resultLog = `🛡️ Kết quả bỏ phiếu: Số phiếu bầu bị hòa (${maxVotes} phiếu ≤ 50%). Dân làng không đạt đa số tuyệt đối, giàn treo cổ bị hủy bỏ!`;
    } else {
      resultLog = `🛡️ Kết quả bỏ phiếu: Không ai bỏ phiếu. Giàn treo cổ bị hủy bỏ!`;
    }

    const winStatus = checkGameEnd(updatedPlayers, nightActions.cupidCouple);
    if (winStatus.isEnd) {
      await updateGameState({
        'phase': 'ENDED',
        'turnEndTime': null,
        'winnerTeam': winStatus.winnerTeam,
        'winnerMsg': winStatus.message,
        'players': updatedPlayers,
        'votes': {}
      }, {
        day: dayNumber,
        phase: 'KẾT THÚC',
        content: `${resultLog} -> ${winStatus.message}`
      });
      return;
    }

    await updateGameState({
      'phase': 'NIGHT_TURN_1',
      'turnEndTime': calculateNextTurnEndTime('NIGHT_TURN_1'),
      'dayNumber': nextDay,
      'players': updatedPlayers,
      'votes': {},
      'nightActions': {
        wolfVotes: {},
        bittenUid: null,
        protectedUid: null,
        seerCheckedUid: null,
        witchSaved: false,
        witchPoisonedUid: null,
        hunterActedThisNight: false,
        cupidCouple: nightActions.cupidCouple || null
      }
    }, {
      day: dayNumber,
      phase: `Ban Ngày ${dayNumber}`,
      content: resultLog
    });
  }, [isHost, gameState?.votes, gameState?.hunterTargetUid, playersMap, dayNumber, cupidCouple, nightActions.cupidCouple, calculateNextTurnEndTime, updateGameState]);

  // ⏱️ EFFECT ĐỒNG BỘ BỘ ĐẾM THỜI GIAN & TỰ ĐỘNG CHUYỂN PHASES
  useEffect(() => {
    const turnEndTime = gameState?.turnEndTime;
    if (!turnEndTime || currentPhase === 'ENDED') {
      setTimeLeft(null);
      return;
    }

    const updateTimer = () => {
      const remaining = Math.max(0, Math.ceil((turnEndTime - Date.now()) / 1000));
      setTimeLeft(remaining);

      if (remaining <= 0) {
        if (isHost) {
          if (currentPhase === 'NIGHT_TURN_1') handleAdvanceToTurn2();
          else if (currentPhase === 'NIGHT_TURN_2') handleAdvanceToDay();
          else if (currentPhase === 'DAY_DISCUSSION') handleAdvanceToVoting();
          else if (currentPhase === 'DAY_VOTING') handleAdvanceToNextNight();
        }
      }
    };

    updateTimer();
    const timerInterval = setInterval(updateTimer, 1000);

    return () => clearInterval(timerInterval);
  }, [
    gameState?.turnEndTime,
    currentPhase,
    isHost,
    handleAdvanceToTurn2,
    handleAdvanceToDay,
    handleAdvanceToVoting,
    handleAdvanceToNextNight
  ]);

  const handleHunterTarget = async (targetUid) => {
    if (myPlayer?.role !== 'HUNTER' || !myPlayer?.isAlive) return;

    const target = playersMap[targetUid];
    const isNight = currentPhase.includes('NIGHT');
    const isDay = currentPhase.includes('DAY');

    if (isNight && hunterActedThisNight) {
      setInfoModalMessage("⚠️ Bạn đã sử dụng lượt đổi mục tiêu ban đêm rồi!");
      return;
    }

    if (isDay && hunterActedThisDay) {
      setInfoModalMessage("⚠️ Bạn đã sử dụng lượt đổi mục tiêu ban ngày rồi!");
      return;
    }

    const updates = {
      'gameState/hunterTargetUid': targetUid
    };

    if (isNight) {
      updates['gameState/nightActions/hunterActedThisNight'] = true;
    } else if (isDay) {
      updates['gameState/hunterActedThisDay'] = true;
    }

    await updateGameState(updates);
    setInfoModalMessage(`🏹 Thợ Săn đã đặt mục tiêu ngắm bắn là: ${target?.fullName}`);
  };

  const handleDayVote = async (targetUid) => {
    if (currentPhase !== 'DAY_VOTING' || !myPlayer?.isAlive) return;
    const target = playersMap[targetUid];
    const newVotes = { ...votes, [activeUid]: targetUid };

    await updateGameState({
      'votes': newVotes
    });

    setInfoModalMessage(`🗳️ Bạn đã bỏ phiếu treo cổ: ${target?.fullName}`);
  };

  const handleWolfBite = async (targetUid) => {
    const target = playersMap[targetUid];
    const newWolfVotes = { ...wolfVotes, [activeUid]: targetUid };

    const voteCounts = {};
    Object.values(newWolfVotes).forEach(tUid => {
      voteCounts[tUid] = (voteCounts[tUid] || 0) + 1;
    });

    let selectedBittenUid = targetUid;
    let maxVotes = 0;
    Object.entries(voteCounts).forEach(([tUid, count]) => {
      if (count > maxVotes) {
        maxVotes = count;
        selectedBittenUid = tUid;
      }
    });

    await updateGameState({
      'nightActions/wolfVotes': newWolfVotes,
      'nightActions/bittenUid': selectedBittenUid
    });

    setInfoModalMessage(`🐺 Bạn đã bầu chọn cắn: ${target?.fullName}`);
  };

  const handleCupidSelect = async (uid) => {
    let newSelection = [...cupidSelection];
    if (newSelection.includes(uid)) {
      newSelection = newSelection.filter(id => id !== uid);
    } else if (newSelection.length < 2) {
      newSelection.push(uid);
    }
    setCupidSelection(newSelection);

    if (newSelection.length === 2) {
      const p1 = playersMap[newSelection[0]];
      const p2 = playersMap[newSelection[1]];

      const updatedPlayers = { ...playersMap };
      if (updatedPlayers[activeUid]) {
        updatedPlayers[activeUid].abilityState = {
          ...updatedPlayers[activeUid].abilityState,
          cupidCompleted: true,
          gameUsesRemaining: 0
        };
      }

      await updateGameState({
        'nightActions/cupidCouple': newSelection,
        'players': updatedPlayers
      }, {
        day: dayNumber,
        phase: `Đêm ${dayNumber}`,
        content: `Thần Tình Yêu đã ghép đôi tình nhân: ${p1?.fullName} (${ROLE_DEFINITIONS[p1?.role]?.name}) ❤️ ${p2?.fullName} (${ROLE_DEFINITIONS[p2?.role]?.name}).`
      });

      setInfoModalMessage(`💘 Đã kết duyên thành công cho ${p1?.fullName} và ${p2?.fullName}!`);
    }
  };

  const handleSeerCheck = async (targetUid) => {
    if (seerHasActedThisNight) return;

    const target = playersMap[targetUid];
    const isWolf = target?.role === 'WEREWOLF';
    const targetRoleName = ROLE_DEFINITIONS[target?.role]?.name || 'Chưa rõ';

    await updateGameState({
      'nightActions/seerCheckedUid': targetUid
    }, {
      day: dayNumber,
      phase: `Đêm ${dayNumber}`,
      content: `Tiên Tri (${myPlayer.fullName}) đã soi người chơi ${target?.fullName} (${targetRoleName} - Phe ${isWolf ? 'Sói' : 'Dân'}).`
    });

    setSeerModalData({
      targetName: target?.fullName,
      isWolf
    });
  };

  const handleGuardProtect = async (targetUid) => {
    if (guardHasActedThisNight) return;

    const target = playersMap[targetUid];
    const targetRoleName = ROLE_DEFINITIONS[target?.role]?.name || 'Chưa rõ';

    const updatedPlayers = { ...playersMap };
    if (updatedPlayers[activeUid]) {
      updatedPlayers[activeUid].abilityState = {
        ...updatedPlayers[activeUid].abilityState,
        lastProtectedUid: targetUid
      };
    }

    await updateGameState({
      'nightActions/protectedUid': targetUid,
      'players': updatedPlayers
    }, {
      day: dayNumber,
      phase: `Đêm ${dayNumber}`,
      content: `Bảo Vệ (${myPlayer.fullName}) đã bảo vệ ${target?.fullName} (${targetRoleName}).`
    });

    setInfoModalMessage(`🛡️ Bạn đã đặt khiên bảo vệ cho ${target?.fullName} đêm nay!`);
  };

  const handleWitchSave = async () => {
    if (witchHasActedThisNight) return;

    const bittenPlayer = playersMap[nightActions.bittenUid];
    const bittenRoleName = ROLE_DEFINITIONS[bittenPlayer?.role]?.name || 'Chưa rõ';

    const updatedPlayers = { ...playersMap };
    if (updatedPlayers[activeUid]) {
      updatedPlayers[activeUid].abilityState = {
        ...updatedPlayers[activeUid].abilityState,
        witchSaveAvailable: false
      };
    }

    await updateGameState({
      'nightActions/witchSaved': true,
      'players': updatedPlayers
    }, {
      day: dayNumber,
      phase: `Đêm ${dayNumber}`,
      content: `Phù Thủy (${myPlayer.fullName}) đã dùng bình thuốc giải cứu sống ${bittenPlayer?.fullName} (${bittenRoleName}).`
    });

    setInfoModalMessage(`🧪 Bạn đã sử dụng bình thuốc giải để cứu ${bittenPlayer?.fullName} (${bittenRoleName})!`);
  };

  const handleWitchPoison = async (targetUid) => {
    if (witchHasActedThisNight) return;

    const target = playersMap[targetUid];
    const targetRoleName = ROLE_DEFINITIONS[target?.role]?.name || 'Chưa rõ';

    const updatedPlayers = { ...playersMap };
    if (updatedPlayers[activeUid]) {
      updatedPlayers[activeUid].abilityState = {
        ...updatedPlayers[activeUid].abilityState,
        witchPoisonAvailable: false
      };
    }

    await updateGameState({
      'nightActions/witchPoisonedUid': targetUid,
      'players': updatedPlayers
    }, {
      day: dayNumber,
      phase: `Đêm ${dayNumber}`,
      content: `Phù Thủy (${myPlayer.fullName}) đã dùng bình độc hạ độc ${target?.fullName} (${targetRoleName}).`
    });

    setInfoModalMessage(`☠️ Bạn đã đầu độc người chơi ${target?.fullName} (${targetRoleName})!`);
  };

  if (!currentRoom || !currentRoom.gameState) return null;

  if (returnedToLobby && currentPhase === 'ENDED') {
    return <WerewolfRoomView />;
  }

  const groupedLogs = gameLogs.reduce((acc, log) => {
    const d = log.day || 1;
    if (!acc[d]) acc[d] = [];
    acc[d].push(log);
    return acc;
  }, {});

  const winnerTeam = gameState.winnerTeam;
  let isMyWin = false;
  if (winnerTeam === 'COUPLE') {
    isMyWin = cupidCouple?.includes(activeUid);
  } else if (winnerTeam === 'WOLF') {
    isMyWin = myPlayer?.role === 'WEREWOLF';
  } else if (winnerTeam === 'HUMAN') {
    isMyWin = myPlayer?.role !== 'WEREWOLF';
  }

  const myRoleInfo = ROLE_DEFINITIONS[myPlayer?.role] || { name: 'Chưa rõ', icon: '❓', team: 'Chưa rõ', desc: '' };

  const handleReturnToLobby = async () => {
    if (isHost) {
      await resetRoomToLobby();
    }
    setReturnedToLobby(true);
  };

  const isNightPhase = currentPhase.includes('NIGHT');

  return (
    <div className="ww-game-container">
      {/* TOAST NOTIFICATION */}
      {toastMessage && (
        <div className="ww-toast-notification animate-fade-in">
          {toastMessage}
        </div>
      )}

      <div className="ww-status-actions">
        <button className="ww-btn secondary" onClick={() => setShowRulesLogsModal(true)}>
          📜 Tiến Trình Game ({gameLogs.length})
        </button>
        <button className="ww-btn danger" onClick={leaveRoom}>
          🚪 Rời Ván
        </button>
      </div>

      {/* THẺ LÁ BÀI CHỨC NĂNG BÍ MẬT CỦA TÔI */}
      <div className="ww-my-role-card-container">
        <div className="ww-role-header">
          <span>Lá Bài Bí Mật Của Bạn:</span>
          <button className="ww-toggle-card-btn" onClick={() => setShowRoleCard(!showRoleCard)}>
            {showRoleCard ? '🙈 Ẩn Bài' : '👁️ Lật Bài Xem'}
          </button>
        </div>

        {showRoleCard ? (
          <div className="ww-secret-role-card reveal">
            <span className="ww-role-icon">{myRoleInfo.icon}</span>
            <h4>{myRoleInfo.name}</h4>
            <span className={`ww-team-badge ${myRoleInfo.team === 'Sói' ? 'wolf' : 'human'}`}>
              Phe: {myRoleInfo.team}
            </span>
            <p className="ww-role-desc">{myRoleInfo.desc}</p>

            {/* THÔNG BÁO TÌNH NHÂN NẰM BÊN TRONG THẺ BÍ MẬT */}
            {isLover && loverPlayer && (
              <div className="ww-lover-card-container animate-fade-in">
                <div className="ww-lover-header">
                  <span>💘 BẠN ĐÃ ĐƯỢC THẦN TÌNH YÊU SE DUYÊN!</span>
                </div>
                <div className="ww-lover-content">
                  <div className="ww-lover-avatar">{loverPlayer.isAlive ? loverPlayer.avatar : '💀'}</div>
                  <div className="ww-lover-info">
                    <h4>Người tình của bạn: <span className="highlight">{loverPlayer.holyName} {loverPlayer.fullName}</span></h4>
                    <p>
                      🎭 Vai trò: <strong>{loverRoleInfo.icon} {loverRoleInfo.name}</strong> ({loverRoleInfo.team})
                    </p>
                    <p className="ww-lover-status">
                      Trạng thái: <strong>{loverPlayer.isAlive ? '💚 Còn sống' : '☠️ Đã chết'}</strong>
                    </p>
                  </div>
                </div>
                <p className="ww-lover-note">
                  ⚠️ <i>Lưu ý: Hai bạn thuộc **Phe Tình Nhân**. Nếu 1 trong 2 người tử vong, người còn lại cũng sẽ chết theo!</i>
                </p>
              </div>
            )}

            {/* Banner Cảnh Báo Cupid Khẩn Cấp */}
            {isCupidNight1Warning && (
              <div className="ww-warning-banner cupid-alert">
                ⚠️ <strong>CẢNH BÁO SỨ MỆNH:</strong> Bạn phải ghép đôi đủ 2 người chơi trong Đêm 1! Nếu hết Lượt 2 chưa chọn, bạn sẽ bị phạt TỬ VONG ngay lập tức.
              </div>
            )}
          </div>
        ) : (
          <div className="ww-secret-role-card hidden" onClick={() => setShowRoleCard(true)}>
            <span>❓ Chạm để mở lá bài</span>
          </div>
        )}
      </div>

      {/* KHU VỰC THAO TÁC CỦA CHỦ PHÒNG */}
      {isHost && currentPhase !== 'ENDED' && (
        <div className="ww-host-control-panel">
          <span>👑 Bảng Điều Khiển Pha Game (Dành Cho Chủ Phòng):</span>
          <div className="ww-host-panel-btns">
            {currentPhase === 'NIGHT_TURN_1' && (
              <button className="ww-btn primary" onClick={handleAdvanceToTurn2}>
                ➔ Chuyển Sang Lượt 2 Ban Đêm (Chức năng)
              </button>
            )}
            {currentPhase === 'NIGHT_TURN_2' && (
              <button className="ww-btn primary" onClick={handleAdvanceToDay}>
                ☀️ Bắt Đầu Ngày Mới & Thông Báo Kết Quả
              </button>
            )}
            {currentPhase === 'DAY_DISCUSSION' && (
              <button className="ww-btn primary" onClick={handleAdvanceToVoting}>
                🗳️ Bắt Đầu Bỏ Phiếu Xử Tử
              </button>
            )}
            {currentPhase === 'DAY_VOTING' && (
              <button className="ww-btn primary" onClick={handleAdvanceToNextNight}>
                🌙 Kết Thúc Bỏ Phiếu & Sang Đêm {dayNumber + 1}
              </button>
            )}
          </div>
        </div>
      )}

      {/* SƠ ĐỒ NGƯỜI CHƠI & BÀN CHƠI */}
      <div className={`ww-game-players-section ${isNightPhase ? 'night' : 'day'}`}>
        {/* Banner trạng thái Pha Game nằm gọn trong khung Bàn Chơi */}
        <div className="ww-game-status-card">
          <div className="ww-status-info">
            <h3>
              {currentPhase === 'NIGHT_TURN_1' && `🌙 Đêm ${dayNumber} - Lượt 1: Sói Đi Săn ${hasCupidInGame ? '& Thần Tình Yêu' : ''}`}
              {currentPhase === 'NIGHT_TURN_2' && `🌙 Đêm ${dayNumber} - Lượt 2: Chức Năng Thức Dậy`}
              {currentPhase === 'DAY_DISCUSSION' && `☀️ Ban Ngày ${dayNumber}: Thảo Luận Bàn Mưu`}
              {currentPhase === 'DAY_VOTING' && `🗳️ Ban Ngày ${dayNumber}: Bỏ Phiếu Xử Tử`}
              {currentPhase === 'ENDED' && `🏆 Ván Game Đã Kết Thúc`}
            </h3>
            <p>
              {currentPhase === 'ENDED' ? 'Ván đấu đã khép lại. Hãy kiểm tra kết quả bên dưới!' : isNightPhase ? 'Đêm tối huyền bí, giữ trật tự và thực hiện chức năng bí mật...' : 'Thảo luận cùng mọi người để tìm ra Ma Sói giấu mặt!'}
            </p>
          </div>

          {/* ⏱️ BỘ ĐẾM THỜI GIAN ĐỒNG BỘ */}
          {currentPhase !== 'ENDED' && (
            <div className="ww-timer-display-box">
              {gameState?.turnEndTime && timeLeft !== null ? (
                <div className={`ww-timer-badge ${timeLeft <= 5 ? 'danger-pulse' : ''}`}>
                  ⏱️ Còn lại: <strong>{timeLeft}s</strong>
                </div>
              ) : (
                <div className="ww-timer-badge free-time">
                  ⏳ Thời gian tự do
                </div>
              )}
            </div>
          )}
        </div>

        <div className="ww-board-title-box">
          <h4>👥 Danh Sách Bàn Chơi ({playersList.filter(p => p.isAlive).length}/{playersList.length} Còn Sống)</h4>
        </div>

        <div className="ww-game-players-grid">
          {playersList.map((player) => {
            const isMe = player.uid === activeUid;
            const isBittenInTurn1 = nightActions.bittenUid === player.uid;
            const isLastProtected = myPlayer?.abilityState?.lastProtectedUid === player.uid;

            const wolvesVotingThisTarget = Object.entries(wolfVotes)
              .filter(([_, tUid]) => tUid === player.uid)
              .map(([wUid]) => playersMap[wUid]?.fullName || 'Sói');

            const isAgreedWolfTarget = isBittenInTurn1 && myPlayer?.role === 'WEREWOLF';

            const votersForThisTarget = Object.entries(votes)
              .filter(([voterUid, tUid]) => tUid === player.uid && playersMap[voterUid]?.isAlive)
              .map(([voterUid]) => playersMap[voterUid]?.fullName || 'Người chơi');

            const isHunterTargeted = hunterTargetUid === player.uid;

            return (
              <div
                key={player.uid}
                className={`ww-game-player-card ${!player.isAlive ? 'dead' : 'alive'} ${isMe ? 'is-me' : ''} ${isAgreedWolfTarget ? 'is-wolf-target' : ''}`}
              >
                <span className="ww-seat-tag">#{player.seatIndex}</span>
                <div className="ww-game-avatar">{player.isAlive ? player.avatar : '💀'}</div>

                <div className="ww-game-player-info">
                  <span className="ww-player-holy">{player.holyName}</span>
                  <span className="ww-player-full">{player.fullName}</span>
                </div>

                <span className={`ww-life-status ${player.isAlive ? 'alive' : 'dead'}`}>
                  {player.isAlive ? '💚 Còn sống' : '☠️ Đã tử vong'}
                </span>

                {myPlayer?.role === 'HUNTER' && isHunterTargeted && player.isAlive && (
                  <div className="ww-hunter-target-badge">
                    🏹 Mục tiêu nhắm bắn
                  </div>
                )}

                {myPlayer?.role === 'WEREWOLF' && player.isAlive && wolvesVotingThisTarget.length > 0 && (
                  <div className="ww-wolf-votes-badge">
                    🐺 {wolvesVotingThisTarget.join(', ')}
                  </div>
                )}

                {currentPhase === 'DAY_VOTING' && player.isAlive && votersForThisTarget.length > 0 && (
                  <div className="ww-day-votes-badge">
                    🗳️ {votersForThisTarget.length} phiếu ({votersForThisTarget.join(', ')})
                  </div>
                )}

                {player.isAlive && myPlayer?.isAlive && currentPhase !== 'ENDED' && (
                  <div className="ww-action-buttons-box">
                    {myPlayer?.role === 'HUNTER' && !isMe && (
                      <button
                        className={`ww-action-btn hunter ${isHunterTargeted ? 'selected' : ''}`}
                        disabled={isNightPhase ? hunterActedThisNight : hunterActedThisDay}
                        onClick={() => handleHunterTarget(player.uid)}
                      >
                        {isHunterTargeted
                          ? '🏹 Đã Nhắm'
                          : (isNightPhase ? hunterActedThisNight : hunterActedThisDay)
                            ? '✔️ Đã đổi'
                            : '🏹 Nhắm Bắn'}
                      </button>
                    )}

                    {currentPhase === 'DAY_VOTING' && !isMe && (
                      <button
                        className={`ww-action-btn vote ${votes[activeUid] === player.uid ? 'selected' : ''}`}
                        onClick={() => handleDayVote(player.uid)}
                      >
                        🗳️ {votes[activeUid] === player.uid ? 'Đã Chọn' : 'Bỏ Phiếu'}
                      </button>
                    )}

                    {currentPhase === 'NIGHT_TURN_1' && myPlayer?.role === 'WEREWOLF' && player.role !== 'WEREWOLF' && (
                      <button
                        className={`ww-action-btn wolf ${wolfVotes[activeUid] === player.uid ? 'selected' : ''}`}
                        onClick={() => handleWolfBite(player.uid)}
                      >
                        🐺 {wolfVotes[activeUid] === player.uid ? 'Đã Chọn Cắn' : 'Chọn Săn'}
                      </button>
                    )}

                    {(currentPhase === 'NIGHT_TURN_1' || currentPhase === 'NIGHT_TURN_2') && dayNumber === 1 && myPlayer?.role === 'CUPID' && (
                      <button
                        className={`ww-action-btn cupid ${cupidSelection.includes(player.uid) ? 'selected' : ''}`}
                        onClick={() => handleCupidSelect(player.uid)}
                      >
                        💘 {cupidSelection.includes(player.uid) ? 'Đã Chọn' : 'Ghép Cặp'}
                      </button>
                    )}

                    {currentPhase === 'NIGHT_TURN_2' && myPlayer?.role === 'SEER' && !isMe && (
                      <button
                        className="ww-action-btn seer"
                        disabled={seerHasActedThisNight}
                        onClick={() => handleSeerCheck(player.uid)}
                      >
                        {seerHasActedThisNight ? '✔️ Đã soi đêm nay' : '🔮 Soi Bản Phe'}
                      </button>
                    )}

                    {currentPhase === 'NIGHT_TURN_2' && myPlayer?.role === 'BODYGUARD' && (
                      <button
                        className="ww-action-btn guard"
                        disabled={guardHasActedThisNight || isLastProtected}
                        onClick={() => handleGuardProtect(player.uid)}
                      >
                        {guardHasActedThisNight
                          ? '✔️ Đã vệ đêm nay'
                          : isLastProtected
                            ? '🚫 Liên tiếp'
                            : '🛡️ Bảo Vệ'}
                      </button>
                    )}

                    {currentPhase === 'NIGHT_TURN_2' && myPlayer?.role === 'WITCH' && (
                      <>
                        {isBittenInTurn1 && (
                          <button
                            className="ww-action-btn witch-save"
                            disabled={witchHasActedThisNight || !myPlayer?.abilityState?.witchSaveAvailable}
                            onClick={handleWitchSave}
                          >
                            🧪 Cứu ({myPlayer?.abilityState?.witchSaveAvailable ? '1' : '0'})
                          </button>
                        )}
                        {!isMe && (
                          <button
                            className="ww-action-btn witch-poison"
                            disabled={witchHasActedThisNight || !myPlayer?.abilityState?.witchPoisonAvailable}
                            onClick={() => handleWitchPoison(player.uid)}
                          >
                            ☠️ Độc ({myPlayer?.abilityState?.witchPoisonAvailable ? '1' : '0'})
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* MODAL KẾT THÚC VÁN GAME */}
      {currentPhase === 'ENDED' && (
        <div className="ww-modal-overlay animate-fade-in">
          <div className="ww-modal-card game-over-modal wide">
            <div className="ww-game-over-header">
              <span className="ww-trophy-icon">🏆</span>
              <h2>KẾT THÚC VÁN GAME</h2>

              <div className={`ww-winner-announcement ${gameState.winnerTeam?.toLowerCase()}`}>
                <h3>{gameState.winnerMsg}</h3>
              </div>

              <div className={`ww-personal-result-badge ${isMyWin ? 'victory' : 'defeat'}`}>
                {isMyWin ? '🎉 BẠN ĐÃ CHIẾN THẮNG!' : '💀 BẠN ĐÃ THẤT BẠI!'}
              </div>
            </div>

            <div className="ww-grouped-logs-container">
              <h4>📜 Lịch Sử Tiến Trình Ván Game (Nhấn từng Ngày để xem chi tiết)</h4>
              {Object.entries(groupedLogs).map(([dayNum, logs]) => {
                // Mặc định false (thu gọn), bấm vào mới chuyển thành true
                const isExpanded = Boolean(expandedDays[dayNum]);

                return (
                  <div key={dayNum} className="ww-day-log-group">
                    <button
                      type="button"
                      className="ww-day-group-header"
                      onClick={() => toggleDayExpand(dayNum)}
                    >
                      <span>🗓️ NGÀY {dayNum} ({logs.length} sự kiện)</span>
                      <span>{isExpanded ? '🔽 Thu gọn' : '▶️ Mở xem'}</span>
                    </button>

                    {isExpanded && (
                      <div className="ww-day-logs-list">
                        {logs.map((log) => (
                          <div key={log.id || `${log.phase}-${log.content}`} className="ww-log-item">
                            <span className="ww-log-phase">{log.phase}</span>
                            <p className="ww-log-content">{log.content}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="ww-modal-actions" style={{ marginTop: '20px', gap: '10px' }}>
              <button className="ww-btn primary start-btn" onClick={handleReturnToLobby}>
                🔄 Về Phòng Chờ
              </button>
              <button className="ww-btn danger" onClick={leaveRoom}>
                🚪 Rời Phòng
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL KẾT QUẢ TIÊN TRI */}
      {seerModalData && (
        <div className="ww-modal-overlay animate-fade-in">
          <div className="ww-modal-card seer-result-card">
            <div className="seer-icon-glow">🔮</div>
            <h3>Kết Quả Tiên Tri</h3>
            <p className="seer-target-name">{seerModalData.targetName}</p>
            <div className={`seer-team-badge ${seerModalData.isWolf ? 'wolf' : 'human'}`}>
              {seerModalData.isWolf ? '🐺 Phe MA SÓI' : '👨‍🌾 Phe DÂN LÀNG'}
            </div>
            <div className="ww-modal-actions" style={{ marginTop: '20px' }}>
              <button className="ww-btn primary" onClick={() => setSeerModalData(null)}>
                Đã Hiểu
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL THÔNG BÁO CHUNG CÁC HÀNH ĐỘNG */}
      {infoModalMessage && (
        <div className="ww-modal-overlay animate-fade-in">
          <div className="ww-modal-card info-result-card">
            <div className="info-icon">✨</div>
            <h3>Thông Báo Hành Động</h3>
            <p className="info-message-content">{infoModalMessage}</p>
            <div className="ww-modal-actions" style={{ marginTop: '16px' }}>
              <button className="ww-btn primary" onClick={() => setInfoModalMessage(null)}>
                Xác Nhận
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL LỊCH SỬ GAME XEM THỦ CÔNG */}
      {showLogsModal && currentPhase !== 'ENDED' && (
        <div className="ww-modal-overlay animate-fade-in">
          <div className="ww-modal-card wide">
            <div className="ww-modal-header-fixed">
              <h3>📜 Lịch Sử Tiến Trình Game</h3>
            </div>

            <div className="ww-grouped-logs-container">
              {Object.keys(groupedLogs).length === 0 ? (
                <p style={{ textAlign: 'center', color: '#64748b', margin: '20px 0' }}>
                  Chưa có sự kiện nào được ghi nhận.
                </p>
              ) : (
                Object.entries(groupedLogs).map(([dayNum, logs]) => {
                  const isExpanded = Boolean(expandedDays[dayNum]);

                  return (
                    <div key={dayNum} className="ww-day-log-group">
                      <button
                        type="button"
                        className="ww-day-group-header"
                        onClick={() => toggleDayExpand(dayNum)}
                      >
                        <span>🗓️ NGÀY {dayNum} ({logs.length} sự kiện)</span>
                        <span>{isExpanded ? '🔽 Thu gọn' : '▶️ Mở xem'}</span>
                      </button>

                      {isExpanded && (
                        <div className="ww-day-logs-list">
                          {logs.map((log) => (
                            <div key={log.id || `${log.phase}-${log.content}`} className="ww-log-item">
                              <span className="ww-log-phase">{log.phase}</span>
                              <p className="ww-log-content">{log.content}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            <div className="ww-modal-actions" style={{ marginTop: '12px' }}>
              <button className="ww-btn primary" onClick={() => setShowRulesLogsModal(false)}>
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WerewolfGameView;